<script lang="ts">
	import type { Device } from 'src/types/Device';
	import { format } from 'src/services/i18n';
	import { showNewLayout } from 'src/stores/toDelete/new_layout';
	import { getContext, onDestroy } from 'svelte';
	import type { Writable } from 'svelte/store';
	import type { SidepanelStickyBottomProps } from 'src/components/Sidepanel/sidepanel-types';
	import { Button, SliderV2, Text } from '@Shelly/shelly-design-system';
	import { getCameraConfig, saveCameraConfig } from '../../helpers/camera-settings-helpers';
	import type { MotionSensitivity } from '../../types/camera-settings';

	export let device: Device;

	const SENSITIVITY_VALUES: readonly MotionSensitivity[] = [
		'very_low',
		'low',
		'medium',
		'high',
		'very_high',
	] as const;
	const DEFAULT_SENSITIVITY: MotionSensitivity = 'medium';
	const DEFAULT_INDEX = SENSITIVITY_VALUES.indexOf(DEFAULT_SENSITIVITY);
	const AUTOSAVE_DELAY = 500;

	const stickyBottomAction =
		getContext<Writable<SidepanelStickyBottomProps | null>>('stickyBottomAction');

	const readSensitivityIndex = (): number => {
		const stored = getCameraConfig(device)?.motion?.sensitivity ?? DEFAULT_SENSITIVITY;
		const index = SENSITIVITY_VALUES.indexOf(stored);
		return index === -1 ? DEFAULT_INDEX : index;
	};

	let sensitivityIndex = readSensitivityIndex();
	let cacheIndex = sensitivityIndex;
	let loading = false;
	let saveTimeout: ReturnType<typeof setTimeout> | undefined;

	$: hasChanges = sensitivityIndex !== cacheIndex;

	$: if (stickyBottomAction) {
		if ($showNewLayout) {
			stickyBottomAction.set({
				onClick: resetToDefaults,
				loading,
				disabled: loading,
				label: $format('button_reset_to_defaults', { default: 'Reset to defaults' }),
			});
		} else {
			stickyBottomAction.set(null);
		}
	}

	const scheduleSave = () => {
		if (saveTimeout) clearTimeout(saveTimeout);
		saveTimeout = setTimeout(save, AUTOSAVE_DELAY);
	};

	const save = async () => {
		if (saveTimeout) {
			clearTimeout(saveTimeout);
			saveTimeout = undefined;
		}
		if (!hasChanges) return;
		loading = true;
		const success = await saveCameraConfig(
			device,
			{ motion: { sensitivity: SENSITIVITY_VALUES[sensitivityIndex] } },
			$format('setting_scc_saved_toast', { default: 'Setting saved successfully.' }),
		);
		if (success) {
			cacheIndex = sensitivityIndex;
		} else {
			sensitivityIndex = cacheIndex;
		}
		loading = false;
	};

	const resetToDefaults = async () => {
		loading = true;
		const success = await saveCameraConfig(
			device,
			{ motion: null },
			$format('camera_motion_sensitivity_reset_success', {
				default: 'Motion sensitivity reset to defaults.',
			}),
		);
		if (success) {
			sensitivityIndex = readSensitivityIndex();
			cacheIndex = sensitivityIndex;
		}
		loading = false;
	};

	onDestroy(() => {
		if (saveTimeout) {
			clearTimeout(saveTimeout);
			save();
		}
		stickyBottomAction?.set(null);
	});
</script>

<Text styleType="h2" class="mb-s">
	{$format('camera_motion_sensitivity', { default: 'Motion sensitivity' })}
</Text>

<SliderV2
	id="camera-motion-sensitivity"
	class="mt-m mb-m"
	bind:value={sensitivityIndex}
	on:value_change={scheduleSave}
	disabled={loading}
	min={0}
	max={4}
	step={1}
	type="incremented"
	title={$format('camera_motion_sensitivity', { default: 'Motion sensitivity' })}
	titleTypography="body-small"
	unit=""
	a11yProps={{ ariaLabel: 'Motion sensitivity', ariaLabelValue: 'Motion sensitivity value' }}
	lineMarkers={[
		{
			value: 0,
			displayValue: $format('camera_motion_sensitivity_very_low', { default: 'Very low' }),
			showLabel: true,
		},
		{
			value: 1,
			displayValue: $format('camera_motion_sensitivity_low', { default: 'Low' }),
			showLabel: true,
		},
		{
			value: 2,
			displayValue: $format('camera_motion_sensitivity_medium', { default: 'Medium' }),
			showLabel: true,
		},
		{
			value: 3,
			displayValue: $format('camera_motion_sensitivity_high', { default: 'High' }),
			showLabel: true,
		},
		{
			value: 4,
			displayValue: $format('camera_motion_sensitivity_very_high', { default: 'Very high' }),
			showLabel: true,
		},
	]}
/>

{#if !$showNewLayout}
	<Button
		class="mt-m"
		variant="secondary"
		width="full"
		size="m"
		{loading}
		disabled={loading}
		on:click={resetToDefaults}
	>
		{$format('button_reset_to_defaults', { default: 'Reset to defaults' })}
	</Button>
{/if}
